import { Avatar, Badge, Box, Card, Flex, Text, useToast } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { fetchApplicationList, updateApplication } from '../../api/recruitment/recruitment'
import {
  Application,
  CandidateStatus,
  CANDIDATE_STATUS_LABELS,
  PIPELINE_COLUMNS,
} from '../../types/recruitment/recruitmentTypes'
import handleToast from '../HandleToast'

const InterviewStageDots: React.FC<{
  value?: number | null
  onChange?: (stage: number) => void
  activeColor?: string
  inactiveColor?: string
}> = ({
  value = 0,
  onChange,
  activeColor = '#40C4AA',
  inactiveColor = '#E2E8F0',
}) => {
  const filledCount = Math.max(0, Math.min(5, value || 0))

  return (
    <Flex direction="column" align="center" minH="24px" justify="center">
      <Flex align="center" gap="6px">
        {Array.from({ length: 5 }).map((_, idx) => {
          const stage = idx + 1
          const isActive = idx < filledCount

          return (
            <Box
              key={stage}
              as="button"
              type="button"
              onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
              onTouchStart={(e: React.TouchEvent<HTMLButtonElement>) => e.stopPropagation()}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation()
                onChange?.(stage)
              }}
              minW="14px"
              maxW="14px"
              h="14px"
              w="14px"
              borderRadius="full"
              borderWidth="1px"
              borderColor={isActive ? activeColor : inactiveColor}
              bg={isActive ? activeColor : 'white'}
              cursor="pointer"
              transition="all 0.15s ease"
              _hover={{ transform: 'scale(1.08)' }}
            />
          )
        })}
      </Flex>
    </Flex>
  )
}

const PipelineBoard: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const applications = useQuery({
    queryKey: ['applications', 'pipeline'],
    queryFn: () =>
      fetchApplicationList({
        search: '',
        page: 1,
        pageSize: 200,
        filters: {},
      }),
    refetchOnWindowFocus: true,
  })

  const updateMutation = useMutation({
    mutationFn: updateApplication,
    onMutate: async (updatedApp: any) => {
      await queryClient.cancelQueries({ queryKey: ['applications', 'pipeline'] })
      const previousData = queryClient.getQueryData(['applications', 'pipeline'])

      queryClient.setQueryData(['applications', 'pipeline'], (old: any) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((app: any) =>
            app.id === updatedApp.id
              ? {
                  ...app,
                  ...(updatedApp.status !== undefined ? { status: updatedApp.status } : {}),
                  ...(updatedApp.interview_stage !== undefined
                    ? { interview_stage: updatedApp.interview_stage }
                    : {}),
                }
              : app,
          ),
        }
      })

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      queryClient.setQueryData(['applications', 'pipeline'], context?.previousData)
      handleToast(
        toast,
        false,
        t('recruitment.pipeline.updateFailed'),
        t('recruitment.pipeline.moveFailed'),
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      queryClient.invalidateQueries({ queryKey: ['candidates'] })
    },
  })

  const allApplications: Application[] = applications.data?.data || []

  const getColumnApplications = (status: CandidateStatus) =>
    allApplications.filter((app: any) => app.status === status)

  const getDaysInStage = (updatedAt: string) => {
    const diff = Date.now() - new Date(updatedAt).getTime()
    return Math.floor(diff / (1000 * 60 * 60 * 24))
  }

  const onDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result
    if (!destination) return

    const newStatus = destination.droppableId as CandidateStatus
    const app = allApplications.find((a: any) => String(a.id) === draggableId)
    if (!app || app.status === newStatus) return

    updateMutation.mutate({ id: draggableId, status: newStatus })
  }

  const columnColors: Record<CandidateStatus, string> = {
    new: 'solid.500',
    screening: 'warning.100',
    interview: 'blue.100',
    offer: 'primary.100',
    hired: 'success.100',
    rejected: 'error.100',
  }

  return (
    <Card width="100%" p="0" overflow="hidden">
      <Flex px="20px" pt="16px" pb="8px" align="center" justify="space-between">
        <Flex align="center" gap="8px">
          <Text textStyle="l-s" fontWeight="600" color="solid.700">
            {t('recruitment.pipeline.title')}
          </Text>
          <Badge bg="solid.50" color="solid.500" borderRadius="6px" fontSize="11px" px="6px" fontWeight="500">
            {allApplications.length} {t('recruitment.pipeline.total')}
          </Badge>
        </Flex>
      </Flex>

      <Box p="20px" overflowX="auto">
        <DragDropContext onDragEnd={onDragEnd}>
          <Flex gap="12px" minW="fit-content">
            {PIPELINE_COLUMNS.map((status) => {
              const columnApps = getColumnApplications(status)

              return (
                <Droppable key={status} droppableId={status}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      minW="240px"
                      w="240px"
                      bg={snapshot.isDraggingOver ? 'solid.25' : 'transparent'}
                      borderRadius="12px"
                      p="8px"
                      transition="background 0.2s"
                    >
                      <Flex align="center" gap="8px" mb="12px" px="4px">
                        <Box w="8px" h="8px" borderRadius="50%" bg={columnColors[status]} />
                        <Text textStyle="l-s" fontWeight="600" color="solid.700">
                          {CANDIDATE_STATUS_LABELS[status]}
                        </Text>
                        <Badge
                          bg="solid.50"
                          color="solid.500"
                          borderRadius="6px"
                          fontSize="11px"
                          px="6px"
                          fontWeight="500"
                        >
                          {columnApps.length}
                        </Badge>
                      </Flex>

                      <Flex direction="column" gap="8px" minH="100px">
                        {columnApps.map((app: any, index: number) => {
                          const candidate = typeof app.candidate === 'object' ? app.candidate : null
                          const vacancy = typeof app.vacancy === 'object' ? app.vacancy : null
                          const interviewStage = app.interview_stage ?? 0

                          return (
                            <Draggable key={String(app.id)} draggableId={String(app.id)} index={index}>
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  bg="white"
                                  borderRadius="10px"
                                  p="12px"
                                  boxShadow={
                                    snapshot.isDragging
                                      ? '0px 8px 24px rgba(0, 0, 0, 0.12)'
                                      : '0px 1px 3px rgba(0, 0, 0, 0.06)'
                                  }
                                  border="1px solid"
                                  borderColor={snapshot.isDragging ? 'primary.100' : 'solid.50'}
                                  transition="box-shadow 0.2s, border-color 0.2s, transform 0.15s"
                                  cursor="grab"
                                  _hover={{
                                    borderColor: 'solid.100',
                                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                                  }}
                                  _active={{ cursor: 'grabbing' }}
                                >
                                  <Flex align="center" justify="space-between" gap="8px" mb="6px">
                                    <Flex align="center" gap="8px" minW={0}>
                                      <Avatar
                                        boxSize="28px"
                                        name={candidate ? `${candidate.first_name} ${candidate.last_name}` : ''}
                                        sx={{
                                          '& > div': {
                                            fontSize: '12px',
                                          },
                                        }}
                                      />
                                      <Text textStyle="l-xs" fontWeight="600" isTruncated>
                                        {candidate
                                          ? `${candidate.first_name} ${candidate.last_name}`
                                          : t('recruitment.pipeline.unknown')}
                                      </Text>
                                    </Flex>

                                    <Box
                                      px="6px"
                                      py="2px"
                                      borderRadius="6px"
                                      color="solid.400"
                                      flexShrink={0}
                                    >
                                      ⋮⋮
                                    </Box>
                                  </Flex>

                                  {vacancy && (
                                    <Text textStyle="b-xs" color="solid.400" mb="6px" isTruncated>
                                      {vacancy.title}
                                    </Text>
                                  )}

                                  {status === 'interview' && (
                                    <Flex align="center" justify="space-between" mb="8px" gap="8px">
                                      <Text textStyle="b-xs" color="solid.400">
                                        Round
                                      </Text>
                                      <InterviewStageDots
                                        value={interviewStage}
                                        onChange={(stage) => {
                                          updateMutation.mutate({
                                            id: app.id,
                                            interview_stage: stage,
                                          })
                                        }}
                                      />
                                    </Flex>
                                  )}

                                  <Flex justify="space-between" align="center">
                                    <Text textStyle="b-xs" color="solid.300" fontSize="11px">
                                      {getDaysInStage(app.updated_at)}
                                      {t('recruitment.pipeline.daysInStage')}
                                    </Text>

                                    {candidate?.source && (
                                      <Badge
                                        bg="solid.25"
                                        color="solid.500"
                                        fontSize="10px"
                                        px="6px"
                                        py="1px"
                                        borderRadius="4px"
                                        textTransform="capitalize"
                                        fontWeight="400"
                                      >
                                        {candidate.source}
                                      </Badge>
                                    )}
                                  </Flex>
                                </Box>
                              )}
                            </Draggable>
                          )
                        })}

                        {columnApps.length === 0 && (
                          <Flex
                            align="center"
                            justify="center"
                            minH="80px"
                            borderRadius="8px"
                            border="1px dashed"
                            borderColor="solid.100"
                            color="solid.300"
                          >
                            <Text textStyle="b-xs" fontSize="11px">
                              {t('recruitment.pipeline.dropHere')}
                            </Text>
                          </Flex>
                        )}

                        {provided.placeholder}
                      </Flex>
                    </Box>
                  )}
                </Droppable>
              )
            })}
          </Flex>
        </DragDropContext>
      </Box>
    </Card>
  )
}

export default PipelineBoard